import { useState } from "react";
import { Flag, Loader2, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { friendlyError } from "@/lib/api";

// "Report this listing as fake/suspicious" — lands as an alert in the admin panel.
export function ReportAbuseButton({ onReport, testid }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return <p className="text-xs text-neutral-500" data-testid={`${testid}-done`}>{t("moderation.reportThanks")}</p>;
  }

  const submit = async () => {
    setSending(true);
    try {
      await onReport(reason);
      setDone(true);
      toast.success(t("moderation.reportSent"));
    } catch (e) {
      toast.error(friendlyError(e, t("moderation.reportError")));
    } finally { setSending(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={testid}
        className="text-xs text-neutral-400 hover:text-[#B91C1C] flex items-center gap-1">
        <Flag className="w-3 h-3" /> {t("moderation.reportAsFake")}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-2 border border-neutral-200 rounded-sm p-2 bg-neutral-50" data-testid={`${testid}-form`}>
      <p className="text-xs font-medium text-neutral-700 flex items-center gap-1"><Flag className="w-3 h-3 text-[#B91C1C]" /> {t("moderation.whySuspicious")}</p>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
        data-testid={`${testid}-reason`} placeholder={t("moderation.reasonPlaceholder")}
        className="w-full rounded-sm border border-neutral-300 bg-white px-2 py-1 text-sm outline-none focus:border-[#0066CC]" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={sending} data-testid={`${testid}-submit`}
          className="flex-1 bg-[#B91C1C] text-white hover:opacity-90 disabled:opacity-60 rounded-sm min-h-[36px] text-sm font-semibold flex items-center justify-center gap-1">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t("moderation.sendReport")}
        </button>
        <button onClick={() => setOpen(false)} className="px-3 text-sm text-neutral-500 hover:text-neutral-800">{t("common.cancel")}</button>
      </div>
    </div>
  );
}

// Self-service withdrawal: the original reporter closes their own listing by
// proving they used the same phone number.
export function WithdrawSection({ onWithdraw, label, testid }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!phone.trim()) { toast.error(t("moderation.enterPhoneUsed")); return; }
    setSending(true);
    try {
      const res = await onWithdraw(phone.trim());
      toast.success(res?.message || t("moderation.withdrawn"));
      setTimeout(() => window.location.assign("/"), 1200);
    } catch (e) {
      toast.error(friendlyError(e, t("moderation.withdrawError")));
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-2" data-testid={testid}>
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid={`${testid}-open`}
          className="text-sm text-neutral-500 hover:text-[#B91C1C] flex items-center gap-1 self-start">
          <ShieldX className="w-4 h-4" /> {label}
        </button>
      ) : (
        <>
          <p className="text-sm font-semibold text-neutral-900">{label}</p>
          <p className="text-xs text-neutral-500">{t("moderation.confirmSamePhone")}</p>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel"
            data-testid={`${testid}-phone`} placeholder="+58..."
            className="w-full min-h-[44px] rounded-sm border border-neutral-300 bg-white px-3 text-base outline-none focus:border-[#0066CC]" />
          <div className="flex gap-2">
            <button onClick={submit} disabled={sending} data-testid={`${testid}-submit`}
              className="flex-1 bg-[#B91C1C] text-white hover:opacity-90 disabled:opacity-60 rounded-sm min-h-[40px] text-sm font-semibold flex items-center justify-center gap-1">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t("moderation.withdrawPost")}
            </button>
            <button onClick={() => setOpen(false)} className="px-3 text-sm text-neutral-500 hover:text-neutral-800">{t("common.cancel")}</button>
          </div>
        </>
      )}
    </div>
  );
}
